import { useState, useRef, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Mic, Volume2, Loader2, Settings, Square, Play, Pause } from "lucide-react";

type VoiceOption = "alloy" | "echo" | "fable" | "onyx" | "nova" | "shimmer";

const VOICE_OPTIONS: { value: VoiceOption; label: string }[] = [
  { value: "alloy", label: "Alloy" },
  { value: "echo", label: "Echo" },
  { value: "fable", label: "Fable" },
  { value: "onyx", label: "Onyx" },
  { value: "nova", label: "Nova" },
  { value: "shimmer", label: "Shimmer" },
];

interface RogersVoiceProps {
  onTranscript?: (text: string) => void;
  className?: string;
}

export default function RogersVoice({ onTranscript, className = "" }: RogersVoiceProps) {
  const [selectedVoice, setSelectedVoice] = useState<VoiceOption>("nova");
  const [showSettings, setShowSettings] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [ttsText, setTtsText] = useState("");
  const [transcript, setTranscript] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const ttsMutation = useMutation({
    mutationFn: async (data: { text: string; voice: string }) => {
      const res = await fetch("/api/voice/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("TTS failed");
      return res.blob();
    },
    onSuccess: (blob) => {
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => setIsPlaying(true);
      audio.onended = () => setIsPlaying(false);
      audio.onpause = () => setIsPlaying(false);
      audio.play();
    },
  });

  const sttMutation = useMutation({
    mutationFn: async (audioBlob: Blob) => {
      const base64 = await blobToBase64(audioBlob);
      const res = await apiRequest("POST", "/api/voice/stt", { audio: base64 });
      return res.json();
    },
    onSuccess: (data: { transcript: string }) => {
      setTranscript(data.transcript);
      onTranscript?.(data.transcript);
    },
  });

  const voiceChatMutation = useMutation({
    mutationFn: async (audioBlob: Blob) => {
      const base64 = await blobToBase64(audioBlob);
      const res = await apiRequest("POST", "/api/voice/chat", { audio: base64, voice: selectedVoice });
      return res.json();
    },
    onSuccess: (data: { transcript: string; audio: string }) => {
      setTranscript(data.transcript);
      onTranscript?.(data.transcript);
      const audioBytes = Uint8Array.from(atob(data.audio), c => c.charCodeAt(0));
      const blob = new Blob([audioBytes], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onplay = () => setIsPlaying(true);
      audio.onended = () => setIsPlaying(false);
      audio.onpause = () => setIsPlaying(false);
      audio.play();
    },
  });

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.start(100);
      setIsRecording(true);
    } catch (err) {
      console.error("Failed to access microphone:", err);
    }
  }, []);

  const stopRecording = useCallback(async () => {
    return new Promise<Blob>((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state !== "recording") {
        setIsRecording(false);
        resolve(new Blob());
        return;
      }
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        recorder.stream.getTracks().forEach((t) => t.stop());
        setIsRecording(false);
        resolve(blob);
      };
      recorder.stop();
    });
  }, []);

  const handleRecordToggle = async () => {
    if (isRecording) {
      const blob = await stopRecording();
      if (blob.size > 0) {
        voiceChatMutation.mutate(blob);
      }
    } else {
      await startRecording();
    }
  };

  const handleTTS = () => {
    if (!ttsText.trim()) return;
    ttsMutation.mutate({ text: ttsText.trim(), voice: selectedVoice });
  };

  const togglePlayback = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
    }
  };

  const isProcessing = sttMutation.isPending || voiceChatMutation.isPending || ttsMutation.isPending;

  return (
    <div className={`bg-card/50 border border-border rounded-2xl p-5 space-y-4 ${className}`} data-testid="rogers-voice-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-purple-500/[0.12] flex items-center justify-center">
            <Volume2 size={15} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground" data-testid="text-voice-title">Rogers Voice AI</h3>
            <p className="text-[10px] text-muted-foreground/70" data-testid="text-voice-subtitle">Speak with Rogers or convert text to speech</p>
          </div>
        </div>
        <Button
          size="icon"
          variant="ghost"
          data-testid="button-voice-settings"
          onClick={() => setShowSettings(!showSettings)}
          className={showSettings ? "text-blue-400" : "text-muted-foreground/70"}
        >
          <Settings size={16} />
        </Button>
      </div>

      {showSettings && (
        <div className="space-y-3 bg-muted/30 border border-border rounded-xl p-4" data-testid="voice-settings">
          <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Voice</label>
          <div className="flex flex-wrap gap-1.5">
            {VOICE_OPTIONS.map((v) => (
              <Badge
                key={v.value}
                data-testid={`button-voice-${v.value}`}
                onClick={() => setSelectedVoice(v.value)}
                className={`cursor-pointer text-[10px] font-bold ${
                  selectedVoice === v.value
                    ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                    : "bg-muted/50 text-muted-foreground/70 border border-border"
                }`}
              >
                {v.label}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Button
            data-testid="button-voice-record"
            onClick={handleRecordToggle}
            disabled={isProcessing}
            variant={isRecording ? "destructive" : "default"}
            className={isRecording ? "animate-pulse" : "bg-blue-600 text-white"}
          >
            {isProcessing ? (
              <Loader2 size={16} className="animate-spin mr-2" />
            ) : isRecording ? (
              <Square size={16} className="mr-2" />
            ) : (
              <Mic size={16} className="mr-2" />
            )}
            {isRecording ? "Stop & Chat" : isProcessing ? "Processing..." : "Voice Chat"}
          </Button>

          {audioRef.current && (
            <Button
              size="icon"
              variant="outline"
              data-testid="button-voice-playback"
              onClick={togglePlayback}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </Button>
          )}
        </div>

        {(voiceChatMutation.isPending || sttMutation.isPending) && (
          <div className="flex items-center gap-2 text-muted-foreground/70 text-xs" data-testid="text-voice-processing">
            <Loader2 size={12} className="animate-spin" />
            {voiceChatMutation.isPending ? "Rogers is listening and thinking..." : "Transcribing..."}
          </div>
        )}

        {transcript && (
          <div className="bg-muted/30 border border-border rounded-xl p-3" data-testid="voice-transcript">
            <span className="text-[10px] font-bold text-muted-foreground/70 uppercase tracking-wider block mb-1">Transcript</span>
            <p className="text-sm text-foreground/70" data-testid="text-voice-transcript-content">{transcript}</p>
          </div>
        )}

        {(voiceChatMutation.isError || sttMutation.isError || ttsMutation.isError) && (
          <p className="text-red-400 text-xs" data-testid="voice-error">
            Voice processing failed. Please try again.
          </p>
        )}
      </div>

      <div className="border-t border-border pt-4 space-y-3">
        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Text to Speech</label>
        <div className="flex gap-2">
          <Input
            data-testid="input-tts-text"
            value={ttsText}
            onChange={(e) => setTtsText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleTTS()}
            placeholder="Type text for Rogers to speak..."
            className="flex-1 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground/70"
          />
          <Button
            data-testid="button-tts-speak"
            onClick={handleTTS}
            disabled={!ttsText.trim() || ttsMutation.isPending}
            className="bg-purple-600 text-white"
          >
            {ttsMutation.isPending ? <Loader2 size={14} className="animate-spin mr-2" /> : <Volume2 size={14} className="mr-2" />}
            Speak
          </Button>
        </div>
      </div>
    </div>
  );
}

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.readAsDataURL(blob);
  });
}
